/**
 * Custom hook for Flow Builder business logic
 * Handles screen management, component operations, and flow state
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useHistoryState } from './useHistoryState';
import { generateId, generateUniqueScreenId } from '../utils/idGeneration';
import { validateBuilderScreens, buildBuilderValidationMap } from '../utils/flowValidation';
import ImageHandler from '../utils/ImageHandler';
import { ensureFootersOnAllScreens, updateAllFooterNavigations, getFooterIndex, chainTerminalToNewScreen, completeSolePointerToDeletedScreen } from '../utils/footerManager';
import '../components/FlowBuilder/flow-components';
import { compile } from '../flow-core/compile';
import { hydrate, blankScreens, hasLoadableScreens } from '../flow-core/hydrate';
import {
  defaultRouting,
  arrayMove,
  forEachBranchList,
  mapBranchLists,
  findComponentInScreenById,
} from '../flow-core/builderModel';

/**
 * @param liveDataConnected the flow's live data has somewhere to go (its Connections): a booking
 *        flow answered by FlowGate, or a server the owner connected. endpointUri alone is only set
 *        once the flow is published, so it cannot be what the builder checks.
 */
export const useFlowBuilder = (currentFlow, reloadNonce = 0, liveDataConnected = false) => {
  // Screens are history-backed so the builder gets undo/redo. setScreens behaves like a normal
  // state setter (value or updater); screensHistory.reset is used for non-undoable flow loads.
  const [screens, setScreens, screensHistory] = useHistoryState(() => blankScreens());
  const resetScreens = screensHistory.reset; // stable; used to load a flow without an undo step
  const [activeScreen, setActiveScreen] = useState('screen_a');
  const [selectedComponent, setSelectedComponent] = useState(null);
  // Flow-level static/dynamic type. Defaults to STATIC; loaded from the flow
  // when editing. Gates the dynamic UI (per-component data-source toggle, Save
  // dialog endpoint section). Setting it is treated as a builder change so the
  // existing dirty/auto-save behaviour (which watches screens/state) triggers.
  const [flowType, setFlowType] = useState('STATIC');

  // Identifies which flow is loaded into the builder. A save round-trips the same flow back via
  // setCurrentFlow (same id, new object); we must NOT reload in that case or we'd round-trip the
  // builder state through JSON and wipe undo history on every (auto-)save. Reload only when a
  // genuinely different flow arrives.
  const loadSignatureRef = useRef(Symbol('initial'));

  // Bumped once per genuine load. Auto-save keys on this so the screens populating from a freshly
  // loaded flow re-baselines the dirty check instead of looking like a user edit (which would
  // otherwise trigger a spurious save + sync the moment a flow opens).
  const [loadGeneration, setLoadGeneration] = useState(0);

  // Load flow data when a different flow is selected for editing. reloadNonce lets callers force a
  // reload of the same flow (e.g. restoring a past version) without a normal save triggering one.
  useEffect(() => {
    // Identity of the flow to load. Saved flows key on id; unsaved flows (templates, blank-from-
    // scratch) have no id, so we key on the flowJson *content*, NOT the object itself, which would
    // coerce to the string "[object Object]" for every template and make the guard treat all
    // templates as the same flow (so selecting a second template would silently load nothing).
    const flowIdentity = currentFlow?.id
      ?? (currentFlow?.flowJson ? JSON.stringify(currentFlow.flowJson) : 'blank');
    const signature = `${flowIdentity}::${reloadNonce}`;
    if (signature === loadSignatureRef.current) {
      return;
    }
    loadSignatureRef.current = signature;
    // A genuine load is about to repopulate screens, re-baseline the auto-save dirty check.
    setLoadGeneration((n) => n + 1);

    // Static/dynamic type loads alongside the screens: from the flow when
    // editing, back to the STATIC default when the builder is cleared. Flows
    // without an explicit flowType (templates, imported JSON) are inferred
    // from the JSON itself, `data_api_version` only exists on dynamic flows.
    setFlowType(
      currentFlow?.flowType
        || (currentFlow?.flowJson?.data_api_version ? 'DYNAMIC' : 'STATIC')
    );

    if (currentFlow && currentFlow.flowJson) {
      if (!hasLoadableScreens(currentFlow.flowJson)) {
        // Flow JSON is empty or invalid (e.g., synced from WhatsApp without full data)
        console.warn('Flow JSON is empty or invalid, initializing with default screen');
      }
      var screensWithFooters = hydrate(currentFlow.flowJson, { fallbackTitle: currentFlow.name });
      resetScreens(screensWithFooters);
      setActiveScreen(screensWithFooters[0]?.id || 'screen_a');
      setSelectedComponent(null);
    } else if (!currentFlow) {
      // New flow (or builder cleared) - reset to a fresh default screen so the
      // previously edited flow is not left on the canvas.
      resetScreens(blankScreens());
      setActiveScreen('screen_a');
      setSelectedComponent(null);
    }
  }, [currentFlow, reloadNonce, resetScreens]);

  // Screen management functions
  const addScreen = () => {
    var existingIds = screens.map(screen => screen.id);
    var newId = generateUniqueScreenId(existingIds);
    var newTitle = `Screen ${screens.length + 1}`;

    var newScreen = {
      id: newId,
      title: newTitle,
      components: [],
      terminal: false,
      routing: defaultRouting(),
    };
    var previousLastId = screens.length > 0 ? screens[screens.length - 1].id : null;
    var newScreens = [...screens, newScreen];
    // Ensure all screens have appropriate Footers (adds to new screen, updates navigation targets).
    var updatedScreens = ensureFootersOnAllScreens(newScreens);
    // If the previously-last screen was terminal (a hand-set/loaded Footer that completes the flow),
    // re-point it at the new screen so the new screen is reachable and becomes the terminal one.
    updatedScreens = chainTerminalToNewScreen(updatedScreens, newScreen.id, previousLastId);
    setScreens(updatedScreens);
    setActiveScreen(newScreen.id);
  };

  const removeScreen = (screenId) => {
    // Don't allow removing the last screen
    if (screens.length <= 1) {
      return;
    }

    var filteredScreens = screens.filter(screen => screen.id !== screenId);
    // If exactly one remaining screen's Footer navigated to the deleted screen, it now completes the
    // flow (its target is gone). If several pointed at it, leave them for the user to resolve.
    var updatedScreens = completeSolePointerToDeletedScreen(filteredScreens, screenId);
    setScreens(updatedScreens);

    // If the removed screen was active, switch to the first screen
    if (activeScreen === screenId) {
      setActiveScreen(filteredScreens[0].id);
    }

    // Clear selected component if it belonged to the removed screen
    setSelectedComponent(null);
  };

  const renameScreen = (screenId, newTitle) => {
    setScreens(prev => prev.map(screen =>
      screen.id === screenId
        ? { ...screen, title: newTitle.trim() || screen.title }
        : screen
    ));
  };

  const reorderScreens = (oldIndex, newIndex) => {
    if (oldIndex === newIndex) return;

    var reorderedScreens = arrayMove(screens, oldIndex, newIndex);
    // Update all auto-managed Footer navigations after reordering
    var updatedScreens = updateAllFooterNavigations(reorderedScreens);
    setScreens(updatedScreens);
  };

  // Component management functions
  const updateComponentProperties = (componentId, properties) => {
    setScreens(prev => prev.map(screen => {
      if (screen.id !== activeScreen) return screen;
      if (screen.components.some(c => c.id === componentId)) {
        return {
          ...screen,
          components: screen.components.map(comp =>
            comp.id === componentId
              ? { ...comp, properties: { ...comp.properties, ...properties } }
              : comp
          ),
        };
      }
      // Search inside container branches (If then/else, Switch cases, generic children)
      return {
        ...screen,
        components: screen.components.map(comp => {
          const newProps = mapBranchLists(comp.properties, list =>
            list.some(sub => sub.id === componentId)
              ? list.map(sub =>
                  sub.id === componentId
                    ? { ...sub, properties: { ...sub.properties, ...properties } }
                    : sub
                )
              : list
          );
          return newProps === comp.properties ? comp : { ...comp, properties: newProps };
        }),
      };
    }));
  };

  const deleteComponent = (componentId) => {
    setScreens(prev => prev.map(screen => {
      if (screen.id !== activeScreen) return screen;
      if (screen.components.some(c => c.id === componentId && c.type === 'Footer')) return screen;
      if (screen.components.some(c => c.id === componentId)) {
        return { ...screen, components: screen.components.filter(c => c.id !== componentId) };
      }
      // Search inside container branches (If then/else, Switch cases, generic children)
      return {
        ...screen,
        components: screen.components.map(comp => {
          const newProps = mapBranchLists(comp.properties, list =>
            list.some(sub => sub.id === componentId)
              ? list.filter(sub => sub.id !== componentId)
              : list
          );
          return newProps === comp.properties ? comp : { ...comp, properties: newProps };
        }),
      };
    }));
    setSelectedComponent(null);
  };

  const addComponent = (componentType, defaultProperties, insertIndex = null) => {
    const newComponent = {
      id: generateId(),
      type: componentType,
      properties: { ...defaultProperties },
    };

    setScreens(prev => prev.map(screen => {
      if (screen.id !== activeScreen) return screen;
      var footerIdx = getFooterIndex(screen);
      var safeIndex;
      if (insertIndex !== null) {
        safeIndex = footerIdx !== -1 ? Math.min(insertIndex, footerIdx) : insertIndex;
      } else {
        safeIndex = footerIdx !== -1 ? footerIdx : screen.components.length;
      }
      return {
        ...screen,
        components: [
          ...screen.components.slice(0, safeIndex),
          newComponent,
          ...screen.components.slice(safeIndex),
        ],
      };
    }));

    return newComponent;
  };

  // Deep-clone a component, assigning fresh ids to it and every nested branch child so a
  // duplicate never collides with the original (which would break drag-and-drop / selection).
  const cloneComponentWithNewIds = (component) => {
    const cloned = JSON.parse(JSON.stringify(component));
    const regenIds = (comp) => {
      comp.id = generateId();
      forEachBranchList(comp.properties, list => list.forEach(regenIds));
    };
    regenIds(cloned);
    return cloned;
  };

  // Duplicate a top-level component on the active screen, inserting the copy right after it
  // (but always before the Footer). Footers are not duplicable. The clone is built up-front so it
  // can also be selected, the setScreens updater runs later and can't return it.
  const duplicateComponent = (componentId) => {
    const screen = screens.find(s => s.id === activeScreen);
    const source = screen?.components.find(c => c.id === componentId);
    if (!source || source.type === 'Footer') return null;

    const clone = cloneComponentWithNewIds(source);
    setScreens(prev => prev.map(s => {
      if (s.id !== activeScreen) return s;
      const idx = s.components.findIndex(c => c.id === componentId);
      if (idx === -1) return s;
      const footerIdx = getFooterIndex(s);
      let insertAt = idx + 1;
      if (footerIdx !== -1 && insertAt > footerIdx) insertAt = footerIdx;
      return {
        ...s,
        components: [...s.components.slice(0, insertAt), clone, ...s.components.slice(insertAt)],
      };
    }));
    setSelectedComponent(clone);
    return clone;
  };

  // Duplicate a whole screen (with all components, fresh ids), inserting it after the source and
  // making it active. Routing is reset so the copy does not inherit the source's navigation.
  const duplicateScreen = (screenId) => {
    const source = screens.find(s => s.id === screenId);
    if (!source) return null;

    const newScreenId = generateUniqueScreenId(screens.map(s => s.id));
    const clonedComponents = source.components
      .filter(c => c.type !== 'Footer')
      .map(cloneComponentWithNewIds);

    setScreens(prev => {
      const idx = prev.findIndex(s => s.id === screenId);
      if (idx === -1) return prev;
      const newScreen = {
        id: newScreenId,
        title: `${prev[idx].title} (copy)`,
        terminal: prev[idx].terminal || false,
        routing: defaultRouting(),
        components: clonedComponents,
      };
      var withFooters = ensureFootersOnAllScreens([...prev.slice(0, idx + 1), newScreen, ...prev.slice(idx + 1)]);
      // Duplicating the terminal screen extends the flow: re-point the source (old terminal) at the
      // copy so the copy becomes reachable and the new end of the flow. No-op when copying a
      // mid-flow screen (the copy isn't last) or when the source's Footer already navigates.
      return chainTerminalToNewScreen(withFooters, newScreenId, screenId);
    });
    setActiveScreen(newScreenId);
    return newScreenId;
  };

  // In-memory clipboard for copy/paste across screens (not the system clipboard, to avoid
  // permission prompts). Holds a ready-to-insert clone; each paste re-clones for fresh ids.
  const clipboardRef = useRef(null);

  const copyComponent = (componentId) => {
    const screen = screens.find(s => s.id === activeScreen);
    const source = screen?.components.find(c => c.id === componentId);
    if (!source || source.type === 'Footer') return null;
    clipboardRef.current = cloneComponentWithNewIds(source);
    return clipboardRef.current;
  };

  const pasteComponent = () => {
    if (!clipboardRef.current) return null;
    const clone = cloneComponentWithNewIds(clipboardRef.current);
    setScreens(prev => prev.map(s => {
      if (s.id !== activeScreen) return s;
      const footerIdx = getFooterIndex(s);
      const insertAt = footerIdx !== -1 ? footerIdx : s.components.length;
      return { ...s, components: [...s.components.slice(0, insertAt), clone, ...s.components.slice(insertAt)] };
    }));
    setSelectedComponent(clone);
    return clone;
  };

  const reorderComponents = (oldIndex, newIndex) => {
    setScreens(prev => prev.map(screen => {
      if (screen.id !== activeScreen) return screen;
      var reordered = arrayMove(screen.components, oldIndex, newIndex);
      var footerIdx = getFooterIndex({ components: reordered });
      if (footerIdx !== -1 && footerIdx !== reordered.length - 1) {
        reordered = [...reordered.filter((_, i) => i !== footerIdx), reordered[footerIdx]];
      }
      return { ...screen, components: reordered };
    }));
  };

  const applyToBranch = (properties, branchKey, updater) => {
    if (branchKey.startsWith('case:')) {
      const caseId = branchKey.slice(5);
      return {
        ...properties,
        cases: (properties.cases || []).map(c =>
          c.id === caseId ? { ...c, children: updater(c.children || []) } : c
        ),
      };
    }
    return { ...properties, [branchKey]: updater(properties[branchKey] || []) };
  };

  const addComponentToBranch = (parentId, branchKey, componentType, defaultProperties, insertIndex) => {
    const newSub = { id: generateId(), type: componentType, properties: { ...defaultProperties } };
    setScreens(prev => prev.map(screen =>
      screen.id === activeScreen
        ? {
            ...screen,
            components: screen.components.map(comp =>
              comp.id === parentId
                ? {
                    ...comp,
                    properties: applyToBranch(comp.properties, branchKey, existing =>
                      insertIndex != null
                        ? [...existing.slice(0, insertIndex), newSub, ...existing.slice(insertIndex)]
                        : [...existing, newSub]
                    ),
                  }
                : comp
            ),
          }
        : screen
    ));
  };

  const removeComponentFromBranch = (parentId, branchKey, nestedId) => {
    setScreens(prev => prev.map(screen =>
      screen.id === activeScreen
        ? {
            ...screen,
            components: screen.components.map(comp =>
              comp.id === parentId
                ? {
                    ...comp,
                    properties: applyToBranch(comp.properties, branchKey, existing =>
                      existing.filter(sub => sub.id !== nestedId)
                    ),
                  }
                : comp
            ),
          }
        : screen
    ));
    setSelectedComponent(prev => prev?.id === nestedId ? null : prev);
  };

  const reorderBranchComponents = (parentId, branchKey, oldIndex, newIndex) => {
    setScreens(prev => prev.map(screen =>
      screen.id === activeScreen
        ? {
            ...screen,
            components: screen.components.map(comp =>
              comp.id === parentId
                ? {
                    ...comp,
                    properties: applyToBranch(comp.properties, branchKey, existing =>
                      arrayMove(existing, oldIndex, newIndex)
                    ),
                  }
                : comp
            ),
          }
        : screen
    ));
  };

  const updateScreenRouting = (screenId, routing) => {
    setScreens(prev => prev.map(screen =>
      screen.id === screenId
        ? { ...screen, routing: { ...screen.routing, ...routing } }
        : screen
    ));
  };

  // Flow validation
  const validateFlow = () => {
    return validateBuilderScreens(screens);
  };

  // Per-component / per-screen validation for inline display. Recomputed
  // whenever screens change so canvas indicators and the summary panel stay
  // in sync with edits.
  const validationMap = useMemo(
    () => buildBuilderValidationMap(screens, { hasEndpoint: !!currentFlow?.endpointUri || liveDataConnected }),
    [screens, currentFlow?.endpointUri, liveDataConnected]
  );

  // Flow JSON generation. The serializer itself lives in flow-core (the backend
  // runs the same code); image processing stays here because it needs browser
  // APIs and is the only asynchronous part.
  const generateFlowJson = async () => {
    const flowJson = compile(screens);

    // Process all images to convert URLs to base64 format for WhatsApp compatibility
    try {
      const processedFlow = await ImageHandler.processFlowForWhatsApp(flowJson);
      return processedFlow;
    } catch (error) {
      console.warn('Error processing images in flow:', error.message);
      // Return original flow if image processing fails
      return flowJson;
    }
  };


  // Current screen getter
  const currentScreen = screens.find(screen => screen.id === activeScreen);

  // Resolve the selected component live from current state each render. `selectedComponent` is
  // stored as an object reference when the user clicks; once `screens` is replaced (e.g. an edit, or
  // the terminal-Footer re-chain on addScreen) that reference goes stale and the properties panel
  // shows outdated values. We look it up by id across ALL screens (the selection may live on a
  // screen that is no longer the active one, adding a screen re-points the previous screen's Footer
  // and switches away from it), including inside If/Switch branches, so every consumer (panel,
  // keyboard shortcuts) stays on current data. Falls back to the stored object for the one render
  // where a freshly-created component is selected before it lands in `screens`.
  let liveSelectedComponent = null;
  if (selectedComponent) {
    for (const screen of screens) {
      const found = findComponentInScreenById(screen, selectedComponent.id);
      if (found) {
        liveSelectedComponent = found;
        break;
      }
    }
    if (!liveSelectedComponent) {
      liveSelectedComponent = selectedComponent;
    }
  }

  return {
    // State
    screens,
    activeScreen,
    selectedComponent: liveSelectedComponent,
    currentScreen,
    flowType,
    setFlowType,
    loadGeneration,

    // Screen management
    addScreen,
    removeScreen,
    renameScreen,
    reorderScreens,
    duplicateScreen,
    setActiveScreen,

    // Component management
    addComponent,
    updateComponentProperties,
    deleteComponent,
    duplicateComponent,
    copyComponent,
    pasteComponent,
    reorderComponents,
    addComponentToBranch,
    removeComponentFromBranch,
    reorderBranchComponents,
    setSelectedComponent,

    // Flow operations
    validateFlow,
    validationMap,
    generateFlowJson,
    updateScreenRouting,

    // Undo / redo (history-backed screens)
    undo: screensHistory.undo,
    redo: screensHistory.redo,
    canUndo: screensHistory.canUndo,
    canRedo: screensHistory.canRedo,
  };
};
